import { useEffect, useRef } from "react";

/**
 * A refresh on a timer that stops when nobody is looking (0017-v2 §0A).
 *
 * Three screens poll because deliveries move on the scheduler's clock and
 * nothing pushes: Posts every 10 seconds, Calendar every 30, PostDetail every
 * 5. None of them checked whether the tab was visible, so a calendar left open
 * in a background tab went on asking all week — and, before `signMedia` began
 * reusing URLs, went on re-minting a fresh signed URL for every asset in the
 * window each time it did.
 *
 * Browsers throttle background timers but do not stop them, and throttling is
 * neither guaranteed nor uniform. Not running is the only reliable version of
 * not running.
 *
 * **Catch up on return.** A hidden tab that skipped an hour of ticks is stale,
 * so becoming visible refreshes immediately rather than waiting out one more
 * interval. That is the whole reason this is worth a hook: the guard and the
 * catch-up are one decision and getting only the first half gives a screen that
 * lies for thirty seconds after you look at it.
 *
 * `paused` is for a dialog open over the thing being refreshed — a confirmation
 * must not have the row moved out from under it — and is read live, so toggling
 * it does not rebuild the timer.
 */
export function usePoll(
  tick: () => void,
  intervalMs: number,
  options: { paused?: boolean } = {},
): void {
  // Read by the interval without being a dependency of it: a changing callback
  // or a toggled dialog must not restart the clock, or a screen that re-renders
  // often would poll far more than it asked to.
  const latest = useRef(tick);
  latest.current = tick;
  const paused = useRef(false);
  paused.current = options.paused === true;

  useEffect(() => {
    // Prerender runs this module under Node (`build:ssr`). Effects do not run
    // there, but the guard costs nothing and says so out loud.
    if (typeof document === "undefined") return;

    const hidden = () => document.visibilityState === "hidden";
    const run = () => { if (!hidden() && !paused.current) latest.current(); };

    const timer = setInterval(run, intervalMs);
    // Same rule on return as on the clock, `paused` included: a confirmation
    // open over a row must not have that row replaced underneath it just
    // because somebody switched tabs and came back.
    document.addEventListener("visibilitychange", run);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", run);
    };
  }, [intervalMs]);
}
